import type { OnlineOrder } from '@restaurant/api-client';

export interface PaymentPill {
  label: string;
  bg: string;
  color: string;
}

/** Current-locale pill wording (`t.orders.pill`), passed in since this is a pure
 *  util rather than a hook. */
export interface PaymentPillLabels {
  paid: string;
  refunded: string;
  awaiting: string;
  failed: string;
  refundNeeded: string;
}

const COLORS = {
  paid: { bg: '#dcfce7', color: '#15803d' },
  refunded: { bg: '#f1f5f9', color: '#475569' },
  awaiting: { bg: '#fef3c7', color: '#b45309' },
  failed: { bg: '#fee2e2', color: '#b91c1c' },
  refundNeeded: { bg: '#fee2e2', color: '#b91c1c' },
} as const;

/**
 * The payment pill for an order row - or `null` when the row should show none.
 *
 * Rule: only surface money that needs attention or reassures; the expected state
 * shows nothing. Pay-at-handoff rails (cash or card-on-handoff) are collected in
 * person, so PENDING_COLLECTION is the expected state → no pill, never "unpaid".
 * Likewise VOID is a pay-at-handoff order cancelled before any collection: no
 * money was ever due upfront and no refund is owed, so it is NOT a payment
 * failure → no pill (the row already reads "Cancelled"). Mirrors the detail view,
 * which shows a neutral "Void", not "failed".
 */
export function paymentPill(
  order: Pick<OnlineOrder, 'paymentStatus' | 'status'>,
  labels: PaymentPillLabels,
): PaymentPill | null {
  const s = order.paymentStatus;
  // No pill for the pay-at-handoff money states: PENDING_COLLECTION (awaiting
  // in-person collection) and VOID (cancelled before any collection - nothing was
  // owed). Neither is a failure or needs owner attention.
  if (!s || s === 'PENDING_COLLECTION' || s === 'VOID') return null;

  // Cancelled but the money was already taken (PAID, or PARTIALLY_REFUNDED =
  // remainder still owed) → the owner owes a refund. Flagged distinctly so it
  // doesn't blend into ordinary unpaid cancellations.
  if (order.status === 'CANCELLED' && (s === 'PAID' || s === 'PARTIALLY_REFUNDED')) {
    return { label: labels.refundNeeded, ...COLORS.refundNeeded };
  }

  switch (s) {
    case 'PAID':
      return { label: labels.paid, ...COLORS.paid };
    case 'REFUNDED':
    case 'PARTIALLY_REFUNDED':
      return { label: labels.refunded, ...COLORS.refunded };
    // Manual bank-transfer proof the owner bounced - a genuine payment problem.
    case 'REJECTED':
      return { label: labels.failed, ...COLORS.failed };
    // AWAITING_PROOF / PROOF_SUBMITTED / PROCESSING / ON_HOLD: a prepaid rail
    // (bank transfer / card) still owed before handoff - genuine attention.
    default:
      return { label: labels.awaiting, ...COLORS.awaiting };
  }
}
